import { useUserTier } from '@heidi/commerce/data';
import {
  useQueryEvidenceUsageStats,
  useQueryTeamEvidenceUsageStats,
} from '@heidi/evidence/data';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';

type UseTrialRecapUsageStatsResult = {
  userUsageStats: ReturnType<typeof useQueryEvidenceUsageStats>['data'];
  teamUsageStats: ReturnType<typeof useQueryTeamEvidenceUsageStats>['data'];
  usageStats: ReturnType<typeof useQueryEvidenceUsageStats>['data'];
  isNoUsage: boolean;
  isLowUsage: boolean;
  shouldUseTeamStats: boolean;
  teamId: string | undefined;
  isClinicianPlan: boolean;
  isTeamAdmin: boolean | undefined;
  isUsageStatsPending: boolean;
  isUsageStatsSuccess: boolean;
};

export function useTrialRecapUsageStats(enabled: boolean): UseTrialRecapUsageStatsResult {
  const { userTier } = useUserTier();
  const { isTeamAdmin, isLoadingTeamDetails, teamDetails } =
    useCombinedQueriesTeamGeneralInfo();

  const isClinicianPlan = userTier === SubscriptionTier.CLINICIAN_PRO;
  const teamId = teamDetails?.team?.id;
  const shouldUseTeamStats = isTeamAdmin === true;

  const {
    data: userUsageStats,
    isSuccess: isUsageStatsSuccess,
    isPending: isUserUsageStatsPending,
  } = useQueryEvidenceUsageStats(enabled);

  const { data: teamUsageStats, isPending: isTeamUsageStatsPending } =
    useQueryTeamEvidenceUsageStats(teamId, enabled && shouldUseTeamStats);

  const isUsageStatsPending =
    isLoadingTeamDetails ||
    (shouldUseTeamStats ? isTeamUsageStatsPending : isUserUsageStatsPending);

  const usageStats = shouldUseTeamStats ? teamUsageStats : userUsageStats;

  const isNoUsage = Boolean(
    !usageStats?.answers_from_evidence &&
    !usageStats?.sessions_with_evidence_queries &&
    !usageStats?.references_used
  );

  // Fewer than 5 Evidence answers means the user hasn't meaningfully engaged;
  // trial recap surfaces forward-looking copy and a secondary CTA instead.
  const isLowUsage = (usageStats?.answers_from_evidence ?? 0) < 5;

  return {
    userUsageStats,
    teamUsageStats,
    usageStats: shouldUseTeamStats ? teamUsageStats : userUsageStats,
    isNoUsage,
    isLowUsage,
    shouldUseTeamStats,
    teamId,
    isClinicianPlan,
    isTeamAdmin,
    isUsageStatsPending,
    isUsageStatsSuccess,
  };
}
